import React, { useContext, useState } from 'react';
import Spin from 'src/components/common/Spin';
import { Alert, message, Table } from 'antd';
import { PaginationConfig, SorterResult, SortOrder } from 'antd/lib/table';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import { arrayWrap } from 'src/utils/arrayWrap';
import { useAppStateContext } from 'src/stateProviders/useAppStateContext';
import { AlertItem, CreateEditAlertDetails } from '../../types/AlertConfiguration';
import { OperationType } from '../../types/SelectedOperation';
import useAlertListFetch from './hooks/useAlertListFetch';
import { ColumnSorting, defaultPagination, getColumns } from './AlertTable.config';
import { createEditAlert, removeAlert, runAlert } from '../../api/alertConfiguration.api';
import { ProductAlertsContext } from '../../ProductAlertsProvider';
import styles from './AlertTable.module.less';

export type AlertQueryParams = {
  sortingcolumn: keyof AlertItem;
  sortingorder: SortOrder;
};

type AlertTableProps = {
  segmentId: string;
  associated: boolean;
  alertName: string;
};

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_NUMBER = 0;

export const AlertTable: React.FC<AlertTableProps> = ({ segmentId, associated, alertName }) => {
  const { user } = useAppStateContext();
  const { setSelectedOperation } = useContext(ProductAlertsContext);
  const [commonLoading, setCommonLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(PAGE_NUMBER);

  const { sortingcolumn = 'alertName', sortingorder = 'ascend' }: AlertQueryParams = useQueryUrlParams();
  const [sorting, setSorting] = useState<ColumnSorting>({ field: sortingcolumn, order: sortingorder });
  const handlePageSizeChanges = (_: number, size: number): void => {
    setPageSize(size);
  };

  const [{ data, loading, error, total }, reloadList] = useAlertListFetch({
    sorting,
    page,
    pageSize,
    segmentId,
    associated,
    alertName,
  });

  const totalRender = (): React.ReactNode => <span>Total Results: {total}</span>;
  const emptyText: React.ReactNode = <p className={styles.textNoResults}>No results found</p>;

  const handlerSortingColumn = (_: unused, __: unused, sorter: SorterResult<AlertItem>): void => {
    const order =
      sorting.field === sorter.columnKey && sorter.order === undefined
        ? sorting.order === 'ascend'
          ? 'descend'
          : 'ascend'
        : sorter.order;

    setSorting({ field: sorter.columnKey as keyof AlertItem, order: order });
  };

  const handleRemoveAlert = async (id: number) => {
    setCommonLoading(true);
    try {
      await removeAlert(id);
      setSelectedOperation(null);
    } catch (e) {
      console.error(e);
    }
    setCommonLoading(false);
  };

  const onRunAlert = async (alertId: number) => {
    try {
      if (user) {
        const alertRunParams = {
          alert_id: String(alertId),
          user_id: user.userId,
          environment: user.environment,
        };
        await runAlert(alertRunParams);
      } else {
        throw new Error('No user data defined');
      }
    } catch (e) {
      message.error(e);
      console.error(e);
    }
  };

  const handleAction = (alert: AlertItem, type: OperationType) => {
    switch (type) {
      case OperationType.EditAlert:
      case OperationType.CloneAlert:
        return setSelectedOperation({ type, alert });
      case OperationType.DeleteAlert:
        return setSelectedOperation({ type, alert, operation: () => handleRemoveAlert(alert.alertId) });
      case OperationType.RunAlert:
        return setSelectedOperation({ type, alert, operation: () => onRunAlert(alert.alertId) });
    }
  };

  const onAssociate = async (alert: AlertItem) => {
    const alertDetails: CreateEditAlertDetails.Request = {
      alertId: alert.alertId,
      segmentId: arrayWrap<number>(alert.segmentId),
      alertName: alert.alertName,
      alertDescription: alert.alertDescription,
      emailSubscription: alert.emailSubscription,
    };
    const i = alertDetails.segmentId.indexOf(Number(segmentId));
    setCommonLoading(true);
    if (i !== -1) {
      alertDetails.segmentId.splice(i, 1);
    } else {
      alertDetails.segmentId.push(Number(segmentId));
    }
    try {
      await createEditAlert(alertDetails);
    } catch (e) {
      console.error(e);
    }
    setCommonLoading(false);
    reloadList();
  };

  const handlerPageChange = (page: number): void => {
    setPage(page);
  };
  const pagination: PaginationConfig = {
    ...defaultPagination,
    pageSize: pageSize,
    current: page,
    total,
    showTotal: totalRender,
    onShowSizeChange: handlePageSizeChanges,
    onChange: handlerPageChange,
  };

  return (
    <>
      {error && (
        <Alert message="Error" description="Something went wrong. Please try again later..." type="error" showIcon />
      )}
      <div style={{ marginBottom: 10 }}>
        <Spin spinning={loading || commonLoading}>
          {data && (
            <Table
              className={styles.table}
              rowKey={(_, index: number): string => String(index)}
              columns={getColumns(sorting, handleAction, associated, onAssociate, reloadList)}
              pagination={pagination}
              dataSource={data}
              onChange={handlerSortingColumn}
              tableLayout={'auto'}
              locale={{ emptyText }}
            />
          )}
        </Spin>
      </div>
    </>
  );
};
